import { useEffect, useState } from 'react';

import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import { AssignmentsObject } from 'src/interfaces/assignment.types';
import AuthService from 'src/services/api/auth.service';
import { hasTxAsset } from 'src/services/entitlement.service';
import {
  useStudentMSAssignmentsQueryState,
  useTeacherMSAssignmentsQueryState,
} from 'src/store/api/assignment';
import {
  useActiveMSAssignmentsStatus,
  useActiveMSAssignmentsSubject,
} from 'src/store/features';
import { isCKLAPilotDomainDisabled } from 'src/utils/date-based-feature-flags';

const SUBJECTS_TO_REPLACE_MAP: Record<string, string> = {
  'Math Elementary': 'Math',
};

export const useAssignmentsFilteringSubjects = () => {
  const [assignmentsSubjects, setAssignmentsSubjects] =
    useState<Array<string> | null>(null);

  const { data: studentAssignments } = useStudentMSAssignmentsQueryState();
  const { data: teacherAssignments } = useTeacherMSAssignmentsQueryState();
  const { data: userAssets } = useUserAssets();

  const isStudent = AuthService.isStudent();

  const activeMSAssignmentsStatus = useActiveMSAssignmentsStatus();
  const activeMSAssignmentsSubject = useActiveMSAssignmentsSubject();

  const userAssignments = isStudent ? studentAssignments : teacherAssignments;

  const getAssignmentsSubjects = (assignments: AssignmentsObject) => {
    const domains = assignments.assignmentDomains?.split(',');

    if (!domains?.length) return;

    const subjectsToReplace = Object.keys(SUBJECTS_TO_REPLACE_MAP);
    subjectsToReplace.forEach((subjectToReplace) => {
      const subjectIndex = domains.indexOf(subjectToReplace);

      if (subjectIndex !== -1) {
        domains[subjectIndex] = SUBJECTS_TO_REPLACE_MAP[subjectToReplace];
      }
    });

    if (isCKLAPilotDomainDisabled()) {
      return [...new Set(domains.filter((d) => d !== 'CKLA Pilot'))];
    }

    return [...new Set(domains)];
  };

  useEffect(() => {
    if (!userAssignments) return;

    // Set assignment subjects only on initial load and on class change.
    if (!activeMSAssignmentsStatus && !activeMSAssignmentsSubject) {
      const subjects = getAssignmentsSubjects(userAssignments);

      if (!subjects?.length) return;

      if (hasTxAsset(userAssets)) {
        const renamedSubjects = subjects.map((subject) =>
          subject.replace(/\bELA(?!R)/g, 'ELAR'),
        );
        return void setAssignmentsSubjects(renamedSubjects);
      }

      setAssignmentsSubjects(subjects);
    }
  }, [
    userAssets,
    userAssignments,
    isStudent,
    activeMSAssignmentsSubject,
    activeMSAssignmentsStatus,
  ]);

  return assignmentsSubjects;
};
